import { useLayoutEffect, useRef } from 'react';

import { useUiStore } from '@/features/settings/ui-store.js';
import { useChatSessionStore } from '@/integration/state/chat-session-store.js';

/**
 * On a user-driven workspace switch: clear the global chat session ids so a stale conversation cannot
 * re-open in the new workspace. Navigation to `/home` is owned by {@link WorkspaceSwitcher} (click
 * handler) and by {@link ChatsPage} as a backup — this hook does not navigate.
 */
export function useWorkspaceChatSwitch(): void {
  const selectedWorkspaceId = useUiStore((state) => state.selectedWorkspaceId);
  const hasHydrated = useUiStore((state) => state.hasHydrated);
  const prevWorkspaceIdRef = useRef<string | null | undefined>(undefined);

  useLayoutEffect(() => {
    if (!hasHydrated) return;
    if (prevWorkspaceIdRef.current === undefined) {
      prevWorkspaceIdRef.current = selectedWorkspaceId;
      return;
    }
    if (prevWorkspaceIdRef.current === selectedWorkspaceId) return;

    prevWorkspaceIdRef.current = selectedWorkspaceId;
    if (useUiStore.getState().lastWorkspaceChangeReason !== 'user') return;

    useChatSessionStore.getState().setActiveConversationId(null);
    useChatSessionStore.getState().setStreamingConvId(null);
  }, [selectedWorkspaceId, hasHydrated]);
}
